/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import get from 'lodash/get';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost, IconButton } from 'office-ui-fabric-react';
import DetailsList from '../DetailsList';
import styles from './StatementList.style';
import { formatDateString, formatNumberWithCommas } from '../../utils';

const selectMenuProps = {
  items: [
    {
      key: 'selectStatementSummary',
      text: 'Select To Download Statement Summary',
      iconProps: { iconName: 'LocationCircle' },
    },
  ],
  directionalHintFixed: true,
};

const StatementList = ({
  items,
  className: classNameProp,
  isContractPage,
  onDownload,
  onSelectStatement,
  selectedStatements,
  onHandleColumnClick,
  selectAllStatements,
  ...other
}) => {
  const columns = {
    select: {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeader,
      arianame: 'icon',
      iconName: selectAllStatements ? 'SkypeCircleCheck' : 'LocationCircle',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: item => {
        const statementId = get(item, '_id');
        const _iconName = selectedStatements.includes(statementId)
          ? 'SkypeCircleCheck'
          : 'LocationCircle';
        return (
          <div>
            <IconButton
              className={styles.selectButton}
              menuProps={selectMenuProps}
              iconProps={{ iconName: _iconName }}
              menuIconProps={{ iconName: '' }}
              ariaLabel="Select"
              onMenuClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onSelectStatement(statementId);
              }}
            />
          </div>
        );
      },
    },
    icon: {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      arianame: 'icon',
      iconName: 'ActivateOrders',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 8,
      maxWidth: 8,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="ActivateOrders" arianame="ActivateOrders" />
          </div>
        );
      },
    },
    statement: {
      key: 'statement',
      name: 'Statement',
      fieldName: 'statement',
      columnActionsMode: 0,
      minWidth: 320,
      maxWidth: 320,
      onRender: item => {
        return (
          <div className={styles.statement}>
            {get(item, 'partner.name')} / {get(item, 'contract.name')}
          </div>
        );
      },
    },
    period: {
      key: 'period',
      name: isContractPage ? 'Statement' : 'Period',
      minWidth: isContractPage ? 320 : null,
      maxWidth: isContractPage ? 320 : null,
      fieldName: 'period',
      columnActionsMode: 0,
      isSorted: true,
      isSortedDescending: true,
      onRender: item => {
        // TODO: Need to include statementFrequency
        return (
          <div>
            {item.status === 'Preview'
              ? formatDateString(item.transactionPeriodCutOff, 'MMM yyyy')
              : formatDateString(item.statementEndDate, 'MMM yyyy')}{' '}
            /{' '}
            {item.status === 'Preview'
              ? 'Month'
              : get(item, 'contract.statementFrequency').charAt(0).toUpperCase() +
                get(item, 'contract.statementFrequency').slice(1)}
          </div>
        );
      },
    },
    payment: {
      key: 'payment',
      name: (
        <div style={{ display: 'flex' }}>
          <div className={styles.paymentHeader}>Payment</div>
          <TooltipHost
            content="Payment is listed in contract currency"
            calloutProps={{
              gapSpace: 5,
              target: '#paymentIcon',
            }}
          >
            <IconButton
              iconProps={{ iconName: 'Info' }}
              ariaLabel="Info"
              className={styles.infoIcon}
              id="paymentIcon"
            />
          </TooltipHost>
        </div>
      ),
      fieldName: 'paymentAmount',
      columnActionsMode: 0,
      onRender: item => {
        const royalty = get(item, 'payment.amount', 0);
        return (
          <div className={styles.payment}>
            {formatNumberWithCommas(royalty.toFixed(2))} {get(item, 'payment.currency')}
          </div>
        );
      },
    },
    status: {
      key: 'paymentStatus',
      name: 'Status',
      columnActionsMode: 0,
      minWidth: isContractPage ? 120 : null,
      maxWidth: isContractPage ? 120 : null,
      onRender: item => {
        const isPaymentSent = get(item, 'payment.primaryStatus') === 'PaymentSent';
        const isPaymentCarriedOver = get(item, 'payment.isPaymentCarriedOver');
        return (
          <div>
            <span
              className={
                isPaymentSent ? styles.paymentStatusSentIcon : styles.paymentStatusInProgressIcon
              }
            >
              <FontIcon
                iconName={isPaymentSent ? 'NavigateBackMirrored' : 'recent'}
                arianame={isPaymentSent ? 'NavigateBackMirrored' : 'recent'}
                id={`${item._id}-paymentStatusIcon`}
              />
            </span>

            {(() => {
              if (isPaymentCarriedOver) return 'No Payment';

              return isPaymentSent ? 'Sent' : 'Scheduled';
            })()}
          </div>
        );
      },
    },
    paymentDate: {
      key: 'paymentDueDate',
      name: 'Payment Date',
      columnActionsMode: 0,
      minWidth: isContractPage ? 130 : null,
      maxWidth: isContractPage ? 130 : null,
      onRender: item => {
        const isPaymentCarriedOver = get(item, 'payment.isPaymentCarriedOver');
        return (
          <span>
            {isPaymentCarriedOver ? '' : formatDateString(get(item, 'payment.paymentDueDate'))}
          </span>
        );
      },
    },
    download: {
      key: 'download',
      name: 'download',
      headerClassName: styles.downloadColumnHeader,
      arianame: 'icon',
      iconName: selectedStatements.length > 0 ? 'Download' : '',
      isIconOnly: true,
      fieldName: 'download',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: item => {
        const downloadUrls = get(item, 'downloadUrls');
        return (
          <div>
            <TooltipHost
              content="Download"
              calloutProps={{
                gapSpace: 5,
              }}
            >
              <IconButton
                tabIndex="0"
                aria-label="Download"
                className={styles.downloadButton}
                iconProps={{ iconName: 'Download' }}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDownload(downloadUrls);
                }}
              />
            </TooltipHost>
          </div>
        );
      },
    },
  };

  const statementsPageColumns = [
    columns.select,
    columns.icon,
    columns.statement,
    columns.period,
    columns.payment,
    columns.status,
    columns.paymentDate,
    columns.download,
  ];

  const contractsPageColumns = [
    columns.icon,
    columns.period,
    columns.payment,
    columns.status,
    columns.paymentDate,
    columns.download,
  ];

  const className = clsx(classNameProp);
  return (
    <DetailsList
      items={items.map(statement => ({
        ...statement,
        key: statement._id,
        link: statement.historicalStatement
          ? undefined
          : `/partner/${get(statement, 'partner.address')}/statements/${statement._id}`,
      }))}
      columns={isContractPage ? contractsPageColumns : statementsPageColumns}
      className={className}
      {...other}
    />
  );
};

StatementList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      statement: PropTypes.string,
      period: PropTypes.string,
      payment: PropTypes.object,
      status: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  isContractPage: PropTypes.bool,
  onDownload: PropTypes.func,
  onSelectStatement: PropTypes.func,
  selectedStatements: PropTypes.arrayOf(PropTypes.any),
  selectAllStatements: PropTypes.bool,
  onHandleColumnClick: PropTypes.func,
};

StatementList.defaultProps = {
  items: [],
  className: '',
  isContractPage: false,
  onDownload: null,
  onSelectStatement: null,
  selectedStatements: [],
  selectAllStatements: false,
  onHandleColumnClick: null,
};

export default StatementList;
